import React, { useEffect, useMemo } from "react";
import "./assets/App.css";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { Provider, useSelector } from "react-redux";
import store from "./store/store";
import { BrowserRouter } from "react-router-dom";
import { RouterComponent } from "./router/index";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import rtlPlugin from "stylis-plugin-rtl";
import SnackbarProvider from "./layout/snackBarProvider";
import { prefixer } from "stylis";
import LiveLocationTracker from "./components/liveLocation/tracker";
import { createAppTheme } from "./theme";
import useNightAccessRestriction from "./components/nightAccessRestriction";
import NightRestrictionScreen from "./components/nightAccessRestriction/NightRestrictionScreen";

const cacheRtl = createCache({
  key: "muirtl",
  stylisPlugins: [prefixer, rtlPlugin],
});

function ThemedApp() {
  const themeMode = useSelector((st) => st?.global?.themeMode) || "light";
  const theme = useMemo(() => createAppTheme(themeMode), [themeMode]);
  const isNightAccessBlocked = useNightAccessRestriction();

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-theme", themeMode);
    root.style.colorScheme = themeMode;
    root.style.setProperty("--app-bg", theme.palette.background.default);
    root.style.setProperty("--app-surface", theme.palette.background.paper);
    root.style.setProperty("--app-border", theme.palette.divider);
    root.style.setProperty("--app-text", theme.palette.text.primary);
    root.style.setProperty("--app-muted", theme.palette.background.muted);
  }, [theme, themeMode]);

  useEffect(() => {
    const ROOT_PATHS = ["/", "/login", "/missions"];

    const closeOpenDialog = () => {
      const dialogs = Array.from(
        document.querySelectorAll(".MuiDialog-root")
      ).filter((el) => el.getAttribute("aria-hidden") !== "true");
      const dialog = dialogs[dialogs.length - 1];
      if (!dialog) return false;

      const closeBtn = dialog.querySelector(
        '[aria-label="close"], [aria-label="بستن"]'
      );
      if (closeBtn) {
        closeBtn.click();
        return true;
      }

      const backdrop = dialog.querySelector(".MuiBackdrop-root");
      if (backdrop) {
        backdrop.click();
        return true;
      }
      return false;
    };

    const onAndroidBack = () => {
      if (closeOpenDialog()) return;
      const path = window.location.pathname.replace(/\/$/, "") || "/";
      if (ROOT_PATHS.includes(path)) return;
      window.history.back();
    };

    window.addEventListener("androidBackPress", onAndroidBack);
    return () => window.removeEventListener("androidBackPress", onAndroidBack);
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {isNightAccessBlocked ? (
        <NightRestrictionScreen />
      ) : (
        <SnackbarProvider>
          <BrowserRouter>
            <LiveLocationTracker />
            <RouterComponent />
          </BrowserRouter>
        </SnackbarProvider>
      )}
    </ThemeProvider>
  );
}

function App() {
  return (
    <CacheProvider value={cacheRtl}>
      <Provider store={store}>
        <ThemedApp />
      </Provider>
    </CacheProvider>
  );
}

export default App;
